import { Typography } from 'components/ui/typography';

type Props = {
  cost: {
    totalCents: number;
    subtotalCents: number;
    shippingCents: number;
    taxCents: number;
    discountCents?: number;
    surchargeCents?: number;
    currency: string;
  };
};

const formatMoney = (cents: number, currency: string) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(cents / 100);

// Receipt-style breakdown — surface card, hairline rows, mono labels
// on the left + tabular nums on the right. Total row is emphasised
// with surface-2 and a stronger top divider so the sum reads as the
// terminal of the list.
export const CostCard = ({ cost }: Props) => {
  type Row = { label: string; cents: number; signed?: 'minus' };
  const rows: Row[] = [
    { label: 'Subtotal', cents: cost.subtotalCents },
    ...(cost.discountCents
      ? [{ label: 'Discount', cents: cost.discountCents, signed: 'minus' as const }]
      : []),
    { label: 'Shipping', cents: cost.shippingCents },
    { label: 'Tax', cents: cost.taxCents },
    ...(cost.surchargeCents ? [{ label: 'Surcharge', cents: cost.surchargeCents }] : []),
  ];

  return (
    <dl className="border border-[var(--line)] bg-[var(--surface)]">
      {rows.map(({ label, cents, signed }) => (
        <div
          key={label}
          className="flex items-center justify-between border-b border-[var(--line)] px-4 py-2.5 last:border-b-0"
        >
          <Typography variant="label" as="dt">
            {label}
          </Typography>
          <Typography variant="mono" as="dd" className="text-sm tabular-nums text-[var(--text)]">
            {signed === 'minus' ? '−' : ''}
            {formatMoney(cents, cost.currency)}
          </Typography>
        </div>
      ))}
      <div className="flex items-center justify-between border-t-2 border-[var(--line-strong)] bg-[var(--surface-2)] px-4 py-3">
        <Typography variant="label" as="dt" className="text-[var(--text)]">
          Total
        </Typography>
        <Typography
          variant="mono"
          as="dd"
          className="text-base font-medium tabular-nums text-[var(--text)]"
        >
          {formatMoney(cost.totalCents, cost.currency)}
        </Typography>
      </div>
    </dl>
  );
};
